import { useState } from "react";
import { toPng } from "html-to-image";

// Export Options
const DEFAULT_OPTIONS = {
  cacheBust: true,
  pixelRatio: 2,
  skipFonts: true,
  onImageErrorHandler: () => {}
};

// Check if the node is an image that can be exported
const filterExportNode = (node) => {
  if (node?.dataset?.exportHide !== undefined) return false;
  return true;
};

export function downloadImage(dataUrl, filename = "export.png") {
  const link = document.createElement("a");
  link.download = filename;
  link.href = dataUrl;
  link.click();
}

export async function shareImageFile(blob, filename, title) {
  if (!blob || typeof navigator === "undefined" || !navigator.share) {
    return false;
  }

  const file = new File([blob], filename, { type: "image/png" });
  const shareData = { files: [file], title: title || filename };

  if (navigator.canShare && !navigator.canShare(shareData)) {
    return false;
  }

  await navigator.share(shareData);
  return true;
}

export async function copyImageToClipboard(blob) {
  if (
    !blob ||
    typeof navigator === "undefined" ||
    !navigator.clipboard ||
    typeof ClipboardItem === "undefined"
  ) {
    return false;
  }

  const pngBlob =
    blob.type === "image/png" ? blob : new Blob([blob], { type: "image/png" });

  await navigator.clipboard.write([
    new ClipboardItem({
      "image/png": pngBlob
    })
  ]);

  return true;
}

// Convert a string to a slug for download
export function slugifyFilename(value, fallback = "export") {
  const slug = (value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || fallback;
}

// Convert a data URL to a blob
async function dataUrlToBlob(dataUrl) {
  const response = await fetch(dataUrl);
  return response.blob();
}

export default function useExportImage() {
  const [exporting, setExporting] = useState(false);

  const captureElement = async (element, options = {}) => {
    if (!element) {
      throw new Error("No element to export");
    }

    setExporting(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const captureOptions = {
        ...DEFAULT_OPTIONS,
        filter: filterExportNode,
        ...options
      };

      const dataUrl = await toPng(element, captureOptions);
      const blob = await dataUrlToBlob(dataUrl);

      return { dataUrl, blob };
    } finally {
      setExporting(false);
    }
  };

  return {
    exporting,
    captureElement,
    downloadImage,
    shareImageFile,
    copyImageToClipboard,
    slugifyFilename
  };
}
